import { ErrorViewModel }                       from '../../..';
import { WidgetInjectionTypeEnum,
         SessionEntity,
         IConversationSessionState }            from '../../..';

export interface IConversationSessionGetters {
  isRequestInProgress(state: IConversationSessionState): boolean;
  isLatestRequestSucceed(state: IConversationSessionState): boolean;
  isWidgetConfigAvailable(state: IConversationSessionState): string | undefined;
  isBotSessionInitialized(state: IConversationSessionState): boolean;
  latestError(state: IConversationSessionState): ErrorViewModel | undefined;
  conversationSessionInfo(state: IConversationSessionState): SessionEntity | undefined;
  nextMessageWatermark(state: IConversationSessionState): number | undefined;
  emotionToPlayId(state: IConversationSessionState): string | undefined;
  isEmbeddedMode(state: IConversationSessionState): boolean | undefined;
  isSessionInitialState(state: IConversationSessionState): boolean;
  isPresentationAvatarAllowed(state: IConversationSessionState): boolean;
  currentSessionId(state: IConversationSessionState): string | undefined;
  suggestionsList(state: IConversationSessionState): string[] | undefined;
  feedbackCurrentSessionScore(state: IConversationSessionState): number;
  suggestionsList(state: IConversationSessionState): string[] | undefined;
}

export const ConversationSessionGetters: IConversationSessionGetters = {
  /**
   * Indicates whether a server request is in progress or not.
   * @returns TRUE when the server has not sent a response to the latest request, FALSE otherwise.
   */
  isRequestInProgress(state: IConversationSessionState): boolean {
    return state.isLoading;
  },
  /**
   * Indicates whether latest user's request is succeed.
   * @returns TRUE when the server has sent a succeeded response to the latest request, FALSE otherwise.
   */
  isLatestRequestSucceed(state: IConversationSessionState): boolean {
    return state.isRequestSucceed;
  },
  /**
   * Indicates whether the widget configuration is available or not. 
   * @returns TRUE when the server has properly initialized the widget configuration parameters, FALSE otherwise.
   */
  isWidgetConfigAvailable(state: IConversationSessionState): string | undefined {
    return state.isWidgetConfigAvailable;
  },
  /**
   * Indicates whether the session Id request is succeed.
   * @returns TRUE when the server has properly initialized the session, FALSE otherwise.
   */
  isBotSessionInitialized(state: IConversationSessionState): boolean {
    return (state.conversationSession !== undefined && state.conversationSession.conversationId !== undefined);
  },
  /**
   * Gets the an instance of ErrorViewModel associated to the latest action error.
   */
  latestError(state: IConversationSessionState): ErrorViewModel | undefined {
    return state.error;
  },
  /**
   * Gets the conversation session information : guestId, Bot name, bot key, etc.
   */
  conversationSessionInfo(state: IConversationSessionState): SessionEntity | undefined {
    return state.conversationSession;
  },
  /**
   * Gets the next message watermark.
   */
  nextMessageWatermark(state: IConversationSessionState): number | undefined {
    return (state.conversationSession !== undefined) ? state.conversationSession.nextMsgWatermark : undefined;
  },
  /**
   * Gets the identifier of the next emotion to play.
   */
  emotionToPlayId(state: IConversationSessionState): string | undefined {
    return (state.conversationSession !== undefined) ? state.emotionToPlayId : undefined;
  },
  /**
   * Gets the configured widget integration mode.
   */
  isEmbeddedMode(state: IConversationSessionState): boolean | undefined {
    return (state.conversationSession !== undefined) ? (state.conversationSession.integrationMode === WidgetInjectionTypeEnum.INTEGRATED) 
                                                     : undefined;
  },
  /**
   * Indicates whether the user has started the conversation.
   * @returns TRUE when the user has not open the presentation widged or sent a message yet, FALSE otherwise.
   */
  isSessionInitialState(state: IConversationSessionState): boolean {
    return (state.conversationSession !== undefined) ? state.conversationSession.isInitialState : false;
  },
  /**
   * Indicates whether the presentation avatar should be displayed or not.
   * @returns TRUE when the user has open the presentation widget or sent a message, FALSE otherwise.
   */
  isPresentationAvatarAllowed(state: IConversationSessionState): boolean {
    if (state.conversationSession) {
      return (state.conversationSession.integrationMode === WidgetInjectionTypeEnum.STANDARD) ? state.conversationSession.isPresentationAvatarAllowed : false;
    }
    return true;
  },
  /**
   * Gets the current conversation session identifier.
   * @returns - the identifier of the active conversation session
   */
  currentSessionId(state: IConversationSessionState): string | undefined {
    return (state.conversationSession !== undefined) ? state.conversationSession.conversationId : undefined;
  },
  /**
   * Gets the suggestions list.
   * @returns - the list of suggestions retrieved from server.
   */
  suggestionsList(state: IConversationSessionState): string[] | undefined {
    return state.suggestions;
  },
  /**
   * Gets the session feedack score.
   * @returns - the identifier of the active conversation session
   */
  feedbackCurrentSessionScore(state: IConversationSessionState): number {
    return (state.conversationSession !== undefined) ? state.conversationSession.score : -1;
  },
};